import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Save, ExternalLink, Link as LinkIcon, Upload, Trash2, ImageIcon, FileSpreadsheet, Bell, PlayCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'osc_admin_token';
const MAX_LOGO_BYTES = 400 * 1024;

export default function SiteSettingsManager() {
  const [bodyshopUrl, setBodyshopUrl] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [reminderTemplate, setReminderTemplate] = useState('');
  const [invoiceTemplate, setInvoiceTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [reportMonth, setReportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const fileRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeaders() });
      setBodyshopUrl(data.bodyshop_url || '');
      setLogoDataUrl(data.logo_data_url || '');
      setReminderTemplate(data.reminder_template || '');
      setInvoiceTemplate(data.invoice_template || '');
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const compressLogo = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 400;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const r = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        let q = 0.9;
        let dataUrl = canvas.toDataURL('image/png');
        while (dataUrl.length > MAX_LOGO_BYTES && q > 0.4) {
          q -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', q);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast({ title: 'Image files only' }); return; }
    try {
      const dataUrl = await compressLogo(f);
      setLogoDataUrl(dataUrl);
      toast({ title: 'Logo ready', description: `${Math.round(dataUrl.length / 1024)} KB` });
    } catch { toast({ title: 'Logo processing failed' }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        bodyshop_url: bodyshopUrl,
        logo_data_url: logoDataUrl,
        reminder_template: reminderTemplate,
        invoice_template: invoiceTemplate,
      }, { headers: authHeaders() });
      toast({ title: 'Settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const previewBodyshop = () => {
    if (!bodyshopUrl) return;
    if (bodyshopUrl.startsWith('http')) window.open(bodyshopUrl, '_blank', 'noopener');
    else window.open(bodyshopUrl, '_blank');
  };

  const downloadGstReport = async () => {
    try {
      const res = await axios.get(`${API}/admin/gst-report`, {
        headers: authHeaders(), params: { month: reportMonth }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `GST-Report-${reportMonth}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast({ title: 'Export failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const runRemindersNow = async () => {
    try {
      await axios.post(`${API}/admin/reminders/run-now`, {}, { headers: authHeaders() });
      toast({ title: 'Reminders triggered', description: 'Check WhatsApp for tomorrow bookings' });
    } catch (e) { toast({ title: 'Failed', description: e?.message }); }
  };

  return (
    <Card className="card-dark p-4 md:p-5 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <LinkIcon className="w-4 h-4 text-pink-400" />
        <h2 className="text-base md:text-lg font-bold text-white">Site Settings</h2>
      </div>
      <p className="text-xs text-gray-400 mb-4">Manage logo, links, WhatsApp templates and reports</p>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Logo */}
        <div>
          <Label className="text-gray-300 text-xs">Company Logo (shown on Invoice/Receipt PDF)</Label>
          <div className="mt-1 rounded-xl border border-dashed border-purple-900/60 bg-[#14141c] p-3">
            {logoDataUrl ? (
              <div className="relative flex items-center justify-center h-32 bg-white rounded-lg">
                <img src={logoDataUrl} alt="logo" className="max-h-28 max-w-full object-contain" />
                <button onClick={() => setLogoDataUrl('')} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 flex items-center justify-center text-red-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="h-32 flex flex-col items-center justify-center text-gray-500 text-xs">
                <ImageIcon className="w-7 h-7 mb-1 opacity-60" />
                No logo uploaded
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onLogoFile} className="hidden" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="mt-2 w-full h-9 text-sm border-purple-900/60 text-gray-200 hover:bg-purple-950/30">
              <Upload className="w-3.5 h-3.5 mr-2" /> Upload Logo
            </Button>
            <p className="text-[10px] text-gray-500 mt-1.5">PNG/JPG · Compressed to max 400×400</p>
          </div>
        </div>

        {/* Links + Templates */}
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Bodyshop URL or Page Path</Label>
            <div className="flex gap-2 mt-1">
              <Input value={bodyshopUrl} onChange={e => setBodyshopUrl(e.target.value)} placeholder="https://... or /bodyshop" className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm" />
              {bodyshopUrl && (
                <Button type="button" variant="outline" onClick={previewBodyshop} className="h-9 shrink-0 border-purple-900/60 text-gray-200 hover:bg-purple-950/30">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Reminder Template Name (WhatsApp)</Label>
            <Input value={reminderTemplate} onChange={e => setReminderTemplate(e.target.value)} placeholder="e.g. booking_reminder_guj (4 params: name, package, date, mobile)" className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Invoice-with-PDF Template Name (optional)</Label>
            <Input value={invoiceTemplate} onChange={e => setInvoiceTemplate(e.target.value)} placeholder="e.g. invoice_with_link (5 params + URL button)" className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
            <p className="text-[10px] text-gray-500 mt-1">Once approved by Meta, I'll wire the invoice link into WhatsApp confirmations.</p>
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-purple-900/40 grid md:grid-cols-2 gap-4">
        {/* GST Report */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-gray-300 text-xs flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5 text-pink-400" /> Monthly GST Report</Label>
            <Input type="month" value={reportMonth} onChange={e => setReportMonth(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
          </div>
          <Button onClick={downloadGstReport} variant="outline" className="h-9 border-purple-900/60 text-gray-200 hover:bg-purple-950/30 shrink-0">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>

        {/* Reminders */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-gray-300 text-xs flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-pink-400" /> Daily Booking Reminders</Label>
            <p className="text-[11px] text-gray-500 mt-1">Runs daily at 10:00 AM IST for next-day PAID bookings</p>
          </div>
          <Button onClick={runRemindersNow} variant="outline" className="h-9 border-purple-900/60 text-gray-200 hover:bg-purple-950/30 shrink-0">
            <PlayCircle className="w-4 h-4 mr-2" /> Run Now
          </Button>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={saving} className="gradient-btn rounded-full h-10 px-6 font-semibold text-sm">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </Card>
  );
}
